import React from 'react';
import { cn } from '@/lib/utils';

interface LoadingSkeletonProps {
  className?: string;
  children?: React.ReactNode;
}

export function LoadingSkeleton({ className, children }: LoadingSkeletonProps) {
  return (
    <div className={cn("animate-pulse bg-muted rounded-md", className)}>
      {children}
    </div>
  );
}

interface TabLoadingStateProps {
  tabName: string;
  message?: string;
}

export function TabLoadingState({ tabName, message = 'Loading...' }: TabLoadingStateProps) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
      <div className="flex items-center space-x-2">
        <div className="w-4 h-4 bg-primary rounded-full animate-bounce [animation-delay:-0.3s]"></div>
        <div className="w-4 h-4 bg-primary rounded-full animate-bounce [animation-delay:-0.15s]"></div>
        <div className="w-4 h-4 bg-primary rounded-full animate-bounce"></div>
      </div>
      <div className="text-center space-y-1">
        <h3 className="text-lg font-medium">Loading {tabName}</h3>
        <p className="text-sm text-muted-foreground">{message}</p>
      </div>
    </div>
  );
}

interface ConversationLoadingSkeletonProps {
  showSidebar?: boolean;
}

export function ConversationLoadingSkeleton({ showSidebar = true }: ConversationLoadingSkeletonProps) {
  return (
    <div className="flex h-full">
      {showSidebar && (
        <div className="w-80 border-r border-border p-4 space-y-4">
          <div className="space-y-2">
            <LoadingSkeleton className="h-10 w-full" />
            <LoadingSkeleton className="h-8 w-3/4" />
          </div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="space-y-2">
                <LoadingSkeleton className="h-6 w-full" />
                <LoadingSkeleton className="h-4 w-2/3" />
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex-1 flex flex-col">
        <div className="border-b border-border p-4">
          <LoadingSkeleton className="h-8 w-1/3" />
        </div>
        <div className="flex-1 p-4 space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="space-y-2">
              <LoadingSkeleton className="h-4 w-1/4" />
              <LoadingSkeleton className="h-20 w-full" />
            </div>
          ))}
        </div>
        <div className="border-t border-border p-4">
          <LoadingSkeleton className="h-12 w-full" />
        </div>
      </div>
    </div>
  );
}

interface ConnectionsLoadingSkeletonProps {}

export function ConnectionsLoadingSkeleton({}: ConnectionsLoadingSkeletonProps) {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <LoadingSkeleton className="h-8 w-48" />
        <LoadingSkeleton className="h-10 w-32" />
      </div>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="border border-border rounded-lg p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <LoadingSkeleton className="h-6 w-32" />
                <LoadingSkeleton className="h-4 w-24" />
              </div>
              <LoadingSkeleton className="h-8 w-8 rounded-full" />
            </div>
            <div className="space-y-2">
              <LoadingSkeleton className="h-4 w-full" />
              <LoadingSkeleton className="h-4 w-3/4" />
            </div>
            <div className="flex space-x-2">
              <LoadingSkeleton className="h-8 w-20" />
              <LoadingSkeleton className="h-8 w-16" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface QueryProgressProps {
  state: 'analyzing' | 'generating' | 'executing' | 'completed' | 'error' | 'idle';
  message?: string;
  progress?: number;
}

export function QueryProgress({ state, message = '', progress = 0 }: QueryProgressProps) {
  if (state === 'idle') return null;

  const getStateInfo = () => {
    switch (state) {
      case 'analyzing':
        return { color: 'bg-blue-500', icon: '🔍', defaultMessage: 'Analyzing your query...' };
      case 'generating':
        return { color: 'bg-yellow-500', icon: '⚡', defaultMessage: 'Generating SQL...' };
      case 'executing':
        return { color: 'bg-purple-500', icon: '🚀', defaultMessage: 'Executing query...' };
      case 'completed':
        return { color: 'bg-green-500', icon: '✅', defaultMessage: 'Query completed!' };
      case 'error':
        return { color: 'bg-red-500', icon: '❌', defaultMessage: 'Query failed' };
      default:
        return { color: 'bg-gray-500', icon: '⏳', defaultMessage: 'Processing...' };
    }
  };

  const stateInfo = getStateInfo();
  const displayMessage = message || stateInfo.defaultMessage;

  return (
    <div className="fixed top-4 right-4 bg-white/95 backdrop-blur-sm border border-[#e6e6ea] rounded-lg shadow-lg p-4 min-w-[300px] z-50 text-[#22212d]">
      <div className="flex items-center space-x-3">
        <div className="text-2xl">{stateInfo.icon}</div>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium">{displayMessage}</span>
            {progress > 0 && (
              <span className="text-sm text-muted-foreground">{progress}%</span>
            )}
          </div>
          {progress > 0 && (
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className={cn("h-2 rounded-full transition-all duration-300", stateInfo.color)}
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
        </div>
        {state === 'analyzing' || state === 'generating' || state === 'executing' ? (
          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        ) : null}
      </div>
    </div>
  );
}